/**
 * Персонал → «Выдать гостю доступ» (Phase 3 гостевого входа).
 *
 * ОТДЕЛЬНЫЙ ЭКРАН-ФОРМА (правило feedback_every_form_is_a_separate_screen):
 * рендерится как самостоятельный экран рабочего пространства с «Назад» снизу;
 * НЕ модалка/инлайн. Роли owner/manager/front_desk (право guest_access.create,
 * гейтится на бэке RouteRoleRule).
 *
 * Поток UX (форма → результат):
 *   1. Форма: номер комнаты (из списка номеров) + фамилия + даты заезд/выезд.
 *   2. POST /v1/guest-access → 201 {grant, tg_deeplink, bot_missing}.
 *   3. Экран результата: СГЕНЕРИРОВАННЫЙ QR (из tg_deeplink, клиентский
 *      рендер) + сам deep-link (копировать) + данные для само-авторизации
 *      (номер/даты/фамилия — гость вводит их на «Я гость отеля»).
 * Кнопки — снизу. «Назад» возвращает к форме / закрывает экран.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CreateGuestAccessResponse } from '@hsc/types';
import { useRooms } from '../rooms/useRooms.js';
import { useCreateGuestAccess } from './useGuestAccess.js';
import { QrCanvas } from './QrCanvas.js';

/** Дата +N дней в формате YYYY-MM-DD. */
function dateOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Человеческая дата (ДД.ММ.ГГГГ). */
function ruDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

export interface GrantAccessPageProps {
  /** Куда возвращает «Назад» (по умолчанию — на дашборд). */
  backTo?: string;
}

export function GrantAccessPage({ backTo = '/' }: GrantAccessPageProps): JSX.Element {
  const navigate = useNavigate();
  const roomsQ = useRooms({ limit: 500 });
  const create = useCreateGuestAccess();

  const [roomNumber, setRoomNumber] = useState('');
  const [surname, setSurname] = useState('');
  const [checkIn, setCheckIn] = useState(dateOffset(0));
  const [checkOut, setCheckOut] = useState(dateOffset(1));
  const [result, setResult] = useState<CreateGuestAccessResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const rooms = useMemo(() => roomsQ.data?.items ?? [], [roomsQ.data]);

  const datesValid = checkIn !== '' && checkOut !== '' && checkOut > checkIn;
  const canSubmit =
    roomNumber.trim() !== '' && surname.trim() !== '' && datesValid && !create.isPending;

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      {
        room_number: roomNumber.trim(),
        surname: surname.trim(),
        check_in: checkIn,
        check_out: checkOut,
      },
      {
        onSuccess: (res) => {
          // Правка Артёма: при выдаче доступа НЕ переходим автоматически в
          // Telegram — просто показываем экран результата с QR-кодом и ссылкой
          // (гость сканирует QR / открывает ссылку сам). Без window.open.
          setResult(res);
        },
      },
    );
  };

  const copyLink = (link: string): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(link).then(
        () => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        },
        () => {
          /* буфер недоступен — игнорируем */
        },
      );
    }
  };

  /* ---------- Экран РЕЗУЛЬТАТА (после выдачи) ---------- */
  if (result) {
    const g = result.grant;
    const link = result.tg_deeplink ?? '';
    return (
      <section data-testid="grant-access-result" className="ga-page">
        <header className="staff-topbar">
          <div>
            <div className="staff-topbar__crumbs">
              <button type="button" className="staff-link" onClick={() => navigate(backTo)}>
                Гостевой доступ
              </button>{' '}
              / Доступ выдан
            </div>
            <h1 className="hsc-page__title staff-topbar__title">Доступ для гостя выдан</h1>
          </div>
        </header>

        <article className="ta-card ga-result-card">
          <p className="ga-result-lead">
            Покажите гостю QR-код или передайте ссылку — он привяжет Telegram и войдёт. Либо гость
            войдёт сам на странице входа («Я гость отеля»), указав данные ниже.
          </p>

          {link ? (
            <div className="ga-qr-wrap" data-testid="grant-qr-wrap">
              <QrCanvas value={link} size={240} testId="grant-qr" />
              <p className="ga-qr-cap">Сканируйте камерой телефона — откроется бот отеля</p>
              <a
                className="staff-btn ga-open-tg"
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="grant-open-telegram"
              >
                Открыть Telegram
              </a>
            </div>
          ) : (
            <div className="ga-bot-missing" data-testid="grant-bot-missing" role="status">
              Telegram-бот отеля ещё не настроен — QR/ссылка недоступны. Гость может войти на
              странице входа по данным ниже («Я гость отеля»).
            </div>
          )}

          {link && (
            <div className="ga-link-row">
              <label className="hsc-input__label" htmlFor="ga-deeplink">
                Ссылка на бота (deep-link)
              </label>
              <div className="ga-link-row__inner">
                <input
                  id="ga-deeplink"
                  className="ta-input ga-link-input"
                  value={link}
                  readOnly
                  data-testid="grant-deeplink"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  type="button"
                  className="staff-btn"
                  onClick={() => copyLink(link)}
                  data-testid="grant-copy"
                >
                  {copied ? 'Скопировано ✓' : 'Скопировать'}
                </button>
              </div>
            </div>
          )}

          <dl className="ga-selfauth" data-testid="grant-selfauth-data">
            <div className="ga-selfauth__title">Данные для входа «Я гость отеля»</div>
            <div className="ga-selfauth__row">
              <dt>Номер</dt>
              <dd data-testid="grant-room">№ {g.room_number}</dd>
            </div>
            <div className="ga-selfauth__row">
              <dt>Фамилия</dt>
              <dd data-testid="grant-surname">{surname.trim()}</dd>
            </div>
            <div className="ga-selfauth__row">
              <dt>Заезд</dt>
              <dd data-testid="grant-checkin">{ruDate(g.check_in)}</dd>
            </div>
            <div className="ga-selfauth__row">
              <dt>Выезд</dt>
              <dd data-testid="grant-checkout">{ruDate(g.check_out)}</dd>
            </div>
          </dl>
        </article>

        <div className="staff-modal__actions ga-actions">
          <button
            type="button"
            className="staff-btn staff-btn--ghost"
            onClick={() => navigate(backTo)}
            data-testid="grant-back"
          >
            Назад
          </button>
          <button
            type="button"
            className="staff-btn"
            data-testid="grant-issue-more"
            onClick={() => {
              create.reset();
              setResult(null);
              setRoomNumber('');
              setSurname('');
              setCheckIn(dateOffset(0));
              setCheckOut(dateOffset(1));
            }}
          >
            Выдать ещё
          </button>
        </div>
      </section>
    );
  }

  /* ---------- Экран ФОРМЫ ---------- */
  return (
    <section data-testid="grant-access-page" className="ga-page">
      <header className="staff-topbar">
        <div>
          <div className="staff-topbar__crumbs">
            <button type="button" className="staff-link" onClick={() => navigate(backTo)}>
              Гостевой доступ
            </button>{' '}
            / Выдать доступ
          </div>
          <h1 className="hsc-page__title staff-topbar__title">Выдать гостю доступ</h1>
        </div>
        <button
          type="button"
          className="staff-btn staff-btn--ghost"
          onClick={() => navigate('/guest-access/pending')}
          data-testid="grant-pending-link"
        >
          Запросы на гостевой доступ
        </button>
      </header>

      <form className="ta-card ga-form" onSubmit={onSubmit} data-testid="grant-access-form">
        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="ga-room">
            Номер комнаты
          </label>
          {rooms.length > 0 ? (
            <select
              id="ga-room"
              className="ta-select"
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
              data-testid="grant-room-select"
              required
            >
              <option value="">— выберите номер —</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.number}>
                  № {r.number}
                </option>
              ))}
            </select>
          ) : (
            <input
              id="ga-room"
              className="ta-input"
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
              placeholder="например, 305"
              data-testid="grant-room-input"
              required
            />
          )}
        </div>

        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="ga-surname">
            Фамилия гостя
          </label>
          <input
            id="ga-surname"
            className="ta-input"
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            placeholder="Иванов"
            autoComplete="off"
            data-testid="grant-surname-input"
            required
          />
        </div>

        <div className="ta-field">
          <span className="hsc-input__label">Даты проживания</span>
          <div className="ga-dates">
            <label className="ga-dates__col">
              <span className="ga-dates__cap">Заезд</span>
              <input
                type="date"
                className="ta-input"
                value={checkIn}
                onChange={(e) => setCheckIn(e.target.value)}
                data-testid="grant-checkin-input"
                required
              />
            </label>
            <label className="ga-dates__col">
              <span className="ga-dates__cap">Выезд</span>
              <input
                type="date"
                className="ta-input"
                value={checkOut}
                min={checkIn}
                onChange={(e) => setCheckOut(e.target.value)}
                data-testid="grant-checkout-input"
                required
              />
            </label>
          </div>
          {!datesValid && (checkIn !== '' || checkOut !== '') && (
            <p className="staff-hint ga-hint--warn" data-testid="grant-dates-hint">
              Дата выезда должна быть позже даты заезда.
            </p>
          )}
        </div>

        {create.isError && (
          <p role="alert" className="staff-invite__msg staff-invite__msg--err" data-testid="grant-error">
            Не удалось выдать доступ. Проверьте номер и даты и попробуйте снова.
          </p>
        )}

        <div className="staff-modal__actions ga-actions">
          <button
            type="button"
            className="staff-btn staff-btn--ghost"
            onClick={() => navigate(backTo)}
            data-testid="grant-form-back"
          >
            Назад
          </button>
          <button type="submit" className="staff-btn" disabled={!canSubmit} data-testid="grant-submit">
            {create.isPending ? 'Выдаём…' : 'Выдать доступ'}
          </button>
        </div>
      </form>
    </section>
  );
}
